import { useEffect, useState } from "react";
import { LOGO_SEAL_WHITE } from "../App";
import { toRoman } from "../utils";


const PILLS = [
  { label: "GENERAL",               value: "General" },
  { label: "HERITAGE ESTATE",       value: "Heritage Estate" },
  { label: "DIGITAL DOCUMENTATION", value: "Digital Documentation" },
  { label: "ARCHIVE ACQUISITION",   value: "Archive Acquisition" },
];

const LABEL = {
  fontFamily: "'EB Garamond', serif",
  fontSize: 11,
  letterSpacing: "0.22em",
  textTransform: "uppercase",
  color: "var(--ink-mute)",
  display: "block",
  marginBottom: 8,
};

const FIELD = {
  background: "transparent",
  border: 0,
  borderBottom: "1px solid var(--ink)",
  fontFamily: "'EB Garamond', serif",
  fontSize: 17,
  color: "var(--ink)",
  outline: "none",
  padding: "8px 0",
  width: "100%",
  boxSizing: "border-box",
};

const OPTIONAL = {
  fontFamily: "'EB Garamond', serif",
  fontStyle: "italic",
  letterSpacing: "0.04em",
  textTransform: "none",
  fontSize: 11,
  color: "var(--ink-mute)",
};

function OrnamentDivider() {
  return (
    <div style={{ display: "flex", alignItems: "center", width: 240, margin: "0 auto" }}>
      <div style={{ flex: 1, height: 1, background: "linear-gradient(to right, transparent, var(--gold-deep))" }} />
      <span style={{ padding: "0 10px", color: "var(--gold-deep)", fontSize: 14, lineHeight: 1 }}>✠</span>
      <div style={{ flex: 1, height: 1, background: "linear-gradient(to left, transparent, var(--gold-deep))" }} />
    </div>
  );
}

export default function EnquiryModal({
  open, onClose,
  eqSubject, setEqSubject,
  eqForm, setEqForm,
  eqStatus, sendEnquiry,
}) {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 599);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 599);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  if (!open) return null;

  const today = new Date();
  const romanDate = `${toRoman(today.getDate())} · ${toRoman(today.getMonth() + 1)} · ${toRoman(today.getFullYear())}`;
  const isSent = eqStatus === "sent";

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 9000,
        background: "rgba(10,10,10,0.55)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "20px",
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: "var(--bg)",
          border: "1px solid var(--ink)",
          borderRadius: 0,
          maxWidth: isMobile ? "100vw" : 720,
          width: "100%",
          position: "relative",
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Close button — fixed relative to panel, not scrolled */}
        <button
          onClick={onClose}
          style={{
            position: "absolute", top: 18, right: 22, zIndex: 2,
            background: "none", border: "none", cursor: "pointer",
            fontFamily: "'EB Garamond', serif", fontSize: 11,
            letterSpacing: "0.24em", textTransform: "uppercase",
            color: "var(--ink-mute)", padding: 0,
          }}
        >
          CLOSE ✕
        </button>

        {/* Scrollable body */}
        <div style={{ overflowY: "auto", flex: 1 }}>
          <style>{`
            @media (max-width: 599px) {
              .eq-letterhead    { padding-left: 24px !important; padding-right: 24px !important; }
              .eq-confirm-body  { padding-left: 24px !important; padding-right: 24px !important; }
              .eq-form-body     { padding-left: 24px !important; padding-right: 24px !important; }
              .eq-footer-strip  { padding-left: 24px !important; padding-right: 24px !important; }
              .eq-form-grid     { grid-template-columns: 1fr !important; }
              .eq-footer-action-row { flex-direction: column !important; align-items: flex-start !important; gap: 12px !important; }
            }
          `}</style>

          {/* Letterhead */}
          <div className="eq-letterhead" style={{ padding: "40px 56px 24px", textAlign: "center" }}>
            <div
              role="img"
              aria-label=""
              style={{
                width: 56, height: 56, margin: "0 auto 16px",
                backgroundColor: "var(--gold-deep)",
                WebkitMaskImage: `url(${LOGO_SEAL_WHITE})`,
                maskImage: `url(${LOGO_SEAL_WHITE})`,
                WebkitMaskSize: "contain", maskSize: "contain",
                WebkitMaskRepeat: "no-repeat", maskRepeat: "no-repeat",
                WebkitMaskPosition: "center", maskPosition: "center",
              }}
            />
            <div style={{
              fontFamily: "'Cinzel', serif",
              fontSize: 18, letterSpacing: "0.30em",
              textTransform: "uppercase", color: "var(--ink)",
            }}>
              ANACHRONA
            </div>
            <div style={{
              fontFamily: "'EB Garamond', serif", fontStyle: "italic",
              fontSize: 13, color: "var(--ink-mute)", letterSpacing: "0.04em",
              margin: "6px 0 20px",
            }}>
              Lourinhã, Costa de Prata · info@anachrona.art
            </div>
            <OrnamentDivider />
          </div>

          {isSent ? (
            /* — Confirmation state — */
            <div className="eq-confirm-body" style={{ padding: "24px 56px 48px", textAlign: "center" }}>
              <h2 style={{
                fontFamily: "'Cormorant Garamond', serif", fontWeight: 300,
                fontSize: 36, textTransform: "uppercase", letterSpacing: "0.04em",
                color: "var(--ink)", margin: "0 0 20px",
              }}>
                Enquiry sent.
              </h2>
              <p style={{
                fontFamily: "'EB Garamond', serif", fontStyle: "italic",
                fontSize: 17, color: "var(--ink-soft)",
                lineHeight: 1.7, maxWidth: 440, margin: "0 auto 40px",
              }}>
                You'll hear back within a day or two. All correspondence is confidential.
              </p>
              <button
                onClick={onClose}
                style={{
                  border: "1px solid var(--gold-deep)",
                  background: "transparent", color: "var(--gold-deep)",
                  fontFamily: "'EB Garamond', serif",
                  fontSize: 12, letterSpacing: "0.22em", textTransform: "uppercase",
                  padding: "10px 32px", borderRadius: 0, cursor: "pointer",
                  transition: "all 0.25s ease",
                }}
                onMouseEnter={e => { e.currentTarget.style.background = "var(--gold-deep)"; e.currentTarget.style.color = "#F8F6F0"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "var(--gold-deep)"; }}
              >
                Return
              </button>
            </div>
          ) : (
            /* — Form — */
            <div className="eq-form-body" style={{ padding: "0 56px" }}>
              <h2 style={{
                fontFamily: "'Cormorant Garamond', serif", fontWeight: 300,
                fontSize: 36, textTransform: "uppercase", letterSpacing: "0.04em",
                color: "var(--ink)", textAlign: "center", margin: "24px 0 10px",
              }}>
                Enquiries
              </h2>
              <p style={{
                fontFamily: "'EB Garamond', serif", fontStyle: "italic",
                fontSize: 15, color: "var(--ink-mute)", textAlign: "center",
                maxWidth: 440, margin: "0 auto 32px",
              }}>
                You'll hear back from Ethan.
              </p>

              {/* Subject pills */}
              <div style={{ marginBottom: 28 }}>
                <div style={{ fontFamily: "'EB Garamond', serif", fontSize: 11, letterSpacing: "0.22em", textTransform: "uppercase", color: "var(--ink-mute)", marginBottom: 12 }}>
                  Enquiry type
                </div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {PILLS.map(({ label, value }) => {
                    const active = eqSubject === value;
                    return (
                      <button
                        key={label}
                        onClick={() => setEqSubject(value)}
                        style={{
                          background: active ? "var(--ink)" : "transparent",
                          color: active ? "#F8F6F0" : "var(--ink)",
                          border: "1px solid var(--ink)",
                          fontFamily: "'EB Garamond', serif",
                          fontSize: 11, letterSpacing: "0.22em", textTransform: "uppercase",
                          padding: "8px 18px", borderRadius: 0, cursor: "pointer",
                          transition: "all 400ms ease",
                        }}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Two-column fields */}
              <div className="eq-form-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 28, marginBottom: 28 }}>

                {/* YOUR NAME */}
                <div>
                  <label style={LABEL}>Your Name</label>
                  <input
                    value={eqForm.name}
                    onChange={e => setEqForm(p => ({ ...p, name: e.target.value }))}
                    style={FIELD}
                  />
                </div>

                {/* AFFILIATION */}
                <div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8 }}>
                    <span style={{ ...LABEL, marginBottom: 0, display: "inline" }}>Affiliation, If Any</span>
                    <span style={OPTIONAL}>Optional</span>
                  </div>
                  <input
                    value={eqForm.role}
                    onChange={e => setEqForm(p => ({ ...p, role: e.target.value }))}
                    style={FIELD}
                  />
                </div>

                {/* EMAIL */}
                <div>
                  <label style={LABEL}>Email</label>
                  <input
                    type="email"
                    value={eqForm.contact}
                    onChange={e => setEqForm(p => ({ ...p, contact: e.target.value }))}
                    style={FIELD}
                  />
                </div>

                {/* TELEPHONE */}
                <div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8 }}>
                    <span style={{ ...LABEL, marginBottom: 0, display: "inline" }}>Telephone</span>
                    <span style={OPTIONAL}>Optional</span>
                  </div>
                  <input
                    type="tel"
                    value={eqForm.telephone || ""}
                    onChange={e => setEqForm(p => ({ ...p, telephone: e.target.value }))}
                    style={FIELD}
                  />
                </div>

                {/* YOUR MESSAGE — full width */}
                <div style={{ gridColumn: "1 / -1" }}>
                  <label style={LABEL}>
                    {eqSubject === "Digital Documentation"
                      ? "Describe the object or collection"
                      : eqSubject === "Heritage Estate"
                      ? "Indicate the type of property and region"
                      : eqSubject === "Archive Acquisition"
                      ? "Indicate the catalogue reference, if known"
                      : "Your message"}
                  </label>
                  <textarea
                    rows={5}
                    value={eqForm.message}
                    onChange={e => setEqForm(p => ({ ...p, message: e.target.value }))}
                    style={{ ...FIELD, resize: "vertical" }}
                  />
                </div>

              </div>

              {/* Confidence note */}
              <p style={{
                fontFamily: "'EB Garamond', serif", fontStyle: "italic",
                fontSize: 13, color: "var(--ink-mute)", lineHeight: 1.6,
                marginBottom: 28,
              }}>
                All correspondence is treated in strict confidence. See our{" "}
                <a href="/privacy" style={{ color: "var(--ink-mute)", textDecoration: "underline" }}>Privacy Policy</a>{" "}
                for how enquiry data is handled.
              </p>

              {/* Validation / error states */}
              {eqStatus === "error" && (
                <div style={{ fontFamily: "'EB Garamond', serif", fontSize: 14, color: "#a04040", marginBottom: 16 }}>
                  Something went wrong. Please try again.
                </div>
              )}
              {typeof eqStatus === "string" && !["sending", "sent", "error"].includes(eqStatus) && eqStatus && (
                <div style={{ fontFamily: "'EB Garamond', serif", fontSize: 14, color: "#a04040", marginBottom: 16 }}>
                  {eqStatus}
                </div>
              )}

              {/* Divider + footer action row */}
              <div className="eq-footer-action-row" style={{
                borderTop: "1px solid var(--rule-strong)", paddingTop: 24,
                display: "flex", justifyContent: "flex-end", alignItems: "center",
                marginBottom: 32,
              }}>
                <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
                  <button
                    onClick={onClose}
                    style={{
                      background: "none", border: "none", cursor: "pointer",
                      fontFamily: "'EB Garamond', serif", fontSize: 11,
                      letterSpacing: "0.22em", textTransform: "uppercase",
                      color: "var(--ink-mute)", padding: 0,
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={sendEnquiry}
                    disabled={eqStatus === "sending"}
                    style={{
                      border: "1px solid var(--ink)",
                      background: "transparent", color: "var(--ink)",
                      fontFamily: "'EB Garamond', serif",
                      fontSize: 12, letterSpacing: "0.22em", textTransform: "uppercase",
                      padding: "10px 24px", borderRadius: 0,
                      cursor: eqStatus === "sending" ? "wait" : "pointer",
                      opacity: eqStatus === "sending" ? 0.5 : 1,
                      transition: "all 0.25s ease",
                    }}
                    onMouseEnter={e => { if (eqStatus !== "sending") { e.currentTarget.style.background = "var(--ink)"; e.currentTarget.style.color = "#F8F6F0"; } }}
                    onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "var(--ink)"; }}
                  >
                    {eqStatus === "sending" ? "Dispatching…" : "Send enquiry"}
                  </button>
                </div>
              </div>
            </div>
          )}

        </div>{/* end scrollable body */}

        {/* Letterpress foot strip — always visible at panel bottom */}
        <div className="eq-footer-strip" style={{
          borderTop: "1px solid var(--rule)",
          padding: "18px 56px",
          display: "flex", justifyContent: "space-between",
          fontFamily: "'EB Garamond', serif",
          fontSize: 10, letterSpacing: "0.24em",
          textTransform: "uppercase", color: "var(--ink-mute)",
          flexShrink: 0,
        }}>
          <span>Form III</span>
          <span>{romanDate}</span>
          <span>Anachrona</span>
        </div>

      </div>
    </div>
  );
}
